import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { validateSerialCode, redeemSerialCode } from '@/lib/serialCode'
import type { SerialCodeValidation } from '@/types'
import { KeyRound, ArrowRight, ArrowLeft, UserPlus, Eye, EyeOff, Ticket, CheckCircle2, Package } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

type Step = 'serial' | 'register'

export default function RegisterPage() {
  const { register } = useAuth()
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>('serial')
  const [serialCode, setSerialCode] = useState('')
  const [serialResult, setSerialResult] = useState<SerialCodeValidation | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleValidateSerial(e: React.FormEvent) {
    e.preventDefault()
    if (!serialCode.trim()) return

    setIsValidating(true)
    try {
      const result = await validateSerialCode(serialCode)
      setSerialResult(result)

      if (result.valid) {
        toast.success('Serial code verified!')
        setStep('register')
      } else {
        toast.error(result.error || 'Invalid serial code')
      }
    } catch {
      toast.error('Failed to validate serial code')
    } finally {
      setIsValidating(false)
    }
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }

    setIsSubmitting(true)
    try {
      const data = await register(email, password, fullName)

      if (data?.user && serialResult?.serial_code_id) {
        await redeemSerialCode(serialResult.serial_code_id, data.user.id)
      }

      toast.success('Account created! Please check your email to confirm.')
      navigate('/login')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left — Branding */}
      <div className="hidden lg:flex lg:w-[480px] bg-primary-900 flex-col justify-between p-10 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-700/30 rounded-full -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-800/40 rounded-full translate-y-1/3 -translate-x-1/4" />

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <Ticket className="w-5 h-5" />
            </div>
            <span className="text-lg font-bold tracking-tight">Ticket CRM</span>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <h1 className="text-3xl font-extrabold leading-tight">
            Get started in<br />
            <span className="text-primary-300">two simple steps.</span>
          </h1>

          {/* Steps indicator */}
          <div className="space-y-4">
            <StepIndicator
              number={1}
              label="Verify serial code"
              description="Enter the code from your product"
              active={step === 'serial'}
              completed={step === 'register'}
            />
            <StepIndicator
              number={2}
              label="Create your account"
              description="Set up your name, email & password"
              active={step === 'register'}
              completed={false}
            />
          </div>
        </div>

        <p className="relative z-10 text-xs text-primary-400">
          © {new Date().getFullYear()} Ticket CRM. All rights reserved.
        </p>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-surface-50">
        <div className="w-full max-w-sm space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 justify-center">
            <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-surface-900">Ticket CRM</span>
          </div>

          {step === 'serial' ? (
            <SerialStep
              serialCode={serialCode}
              setSerialCode={setSerialCode}
              isValidating={isValidating}
              onSubmit={handleValidateSerial}
            />
          ) : (
            <RegisterStep
              fullName={fullName}
              setFullName={setFullName}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              isSubmitting={isSubmitting}
              serialResult={serialResult}
              onSubmit={handleRegister}
              onBack={() => setStep('serial')}
            />
          )}

          <p className="text-center text-sm text-surface-500">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-700 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

/* ── Serial Code Step ── */
function SerialStep({
  serialCode, setSerialCode, isValidating, onSubmit,
}: {
  serialCode: string
  setSerialCode: (v: string) => void
  isValidating: boolean
  onSubmit: (e: React.FormEvent) => void
}) {
  return (
    <>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-surface-900">Verify Serial Code</h2>
        <p className="text-sm text-surface-500">
          Enter the product serial code you received to begin registration
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-5">
        <div className="space-y-1.5">
          <label htmlFor="serial-code" className="block text-sm font-medium text-surface-700">
            Serial Code
          </label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              id="serial-code"
              type="text"
              required
              value={serialCode}
              onChange={(e) => setSerialCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX-XXXX"
              className={cn(
                'w-full h-10 pl-10 pr-3 rounded-md border border-surface-300 bg-white text-sm font-mono tracking-wider',
                'placeholder:text-surface-400 uppercase',
                'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
                'transition-colors'
              )}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isValidating || !serialCode.trim()}
          className={cn(
            'w-full h-10 rounded-md text-sm font-semibold text-white',
            'bg-primary-600 hover:bg-primary-700 active:bg-primary-800',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            'transition-colors flex items-center justify-center gap-2',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {isValidating ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              Verify Code
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>
    </>
  )
}

/* ── Register Step ── */
function RegisterStep({
  fullName, setFullName, email, setEmail, password, setPassword,
  confirmPassword, setConfirmPassword, showPassword, setShowPassword,
  isSubmitting, serialResult, onSubmit, onBack,
}: {
  fullName: string; setFullName: (v: string) => void
  email: string; setEmail: (v: string) => void
  password: string; setPassword: (v: string) => void
  confirmPassword: string; setConfirmPassword: (v: string) => void
  showPassword: boolean; setShowPassword: (v: boolean) => void
  isSubmitting: boolean
  serialResult: SerialCodeValidation | null
  onSubmit: (e: React.FormEvent) => void
  onBack: () => void
}) {
  return (
    <>
      <div className="space-y-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-surface-500 hover:text-surface-700 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
        <h2 className="text-2xl font-bold text-surface-900">Create Account</h2>
        <p className="text-sm text-surface-500">
          Fill in your details to complete registration
        </p>
      </div>

      {/* Linked Products */}
      {serialResult?.products && serialResult.products.length > 0 && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 space-y-2">
          <p className="text-xs font-medium text-primary-700 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Products linked to your serial code
          </p>
          <div className="flex flex-wrap gap-1.5">
            {serialResult.products.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-primary-100 text-primary-800"
              >
                <Package className="w-3 h-3" />
                {p.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Full Name */}
        <div className="space-y-1.5">
          <label htmlFor="reg-name" className="block text-sm font-medium text-surface-700">
            Full Name
          </label>
          <input
            id="reg-name"
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="John Doe"
            className={cn(
              'w-full h-10 px-3 rounded-md border border-surface-300 bg-white text-sm',
              'placeholder:text-surface-400',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
              'transition-colors'
            )}
          />
        </div>

        {/* Email */}
        <div className="space-y-1.5">
          <label htmlFor="reg-email" className="block text-sm font-medium text-surface-700">
            Email
          </label>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className={cn(
              'w-full h-10 px-3 rounded-md border border-surface-300 bg-white text-sm',
              'placeholder:text-surface-400',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
              'transition-colors'
            )}
          />
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <label htmlFor="reg-password" className="block text-sm font-medium text-surface-700">
            Password
          </label>
          <div className="relative">
            <input
              id="reg-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              className={cn(
                'w-full h-10 px-3 pr-10 rounded-md border border-surface-300 bg-white text-sm',
                'placeholder:text-surface-400',
                'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
                'transition-colors'
              )}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="space-y-1.5">
          <label htmlFor="reg-confirm" className="block text-sm font-medium text-surface-700">
            Confirm Password
          </label>
          <input
            id="reg-confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repeat password"
            className={cn(
              'w-full h-10 px-3 rounded-md border border-surface-300 bg-white text-sm',
              'placeholder:text-surface-400',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
              'transition-colors',
              confirmPassword && password !== confirmPassword && 'border-danger-400 focus:ring-danger-500/20 focus:border-danger-500'
            )}
          />
          {confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-danger-600">Passwords do not match</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting || !fullName || !email || !password || password !== confirmPassword}
          className={cn(
            'w-full h-10 rounded-md text-sm font-semibold text-white',
            'bg-primary-600 hover:bg-primary-700 active:bg-primary-800',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            'transition-colors flex items-center justify-center gap-2',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {isSubmitting ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <UserPlus className="w-4 h-4" />
              Create Account
            </>
          )}
        </button>
      </form>
    </>
  )
}

/* ── Step Indicator ── */
function StepIndicator({
  number, label, description, active, completed,
}: {
  number: number; label: string; description: string; active: boolean; completed: boolean
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors',
          completed ? 'bg-primary-400 text-white' :
          active ? 'bg-white text-primary-900' :
          'bg-primary-800 text-primary-400'
        )}
      >
        {completed ? <CheckCircle2 className="w-4 h-4" /> : number}
      </div>
      <div>
        <p className={cn('text-sm font-semibold', active ? 'text-white' : 'text-primary-300')}>
          {label}
        </p>
        <p className="text-xs text-primary-400">{description}</p>
      </div>
    </div>
  )
}
